// To convert a string to have a Id format

import { useMemo } from 'react';

import _ from 'lodash';

import {
  type TypeModel,
  type TypeOrganization,
  type TypeScenario,
} from '../common/dataTypes';
import { useGetProjectById } from '../hooks';
import {
  useGetOrganizations,
  useGetOrgMembers,
} from '../hooks/tanstack/useOrganizationHook';
import { useGetUser } from '../hooks/tanstack/useUserHook';
import { isOrgAdmin, useGetRouteParams } from '../hooks/tanstack/useUtilsHook';
import useUserStore from '../hooks/zustand/useUserStore';
import {
  type Granularity,
  type OrganizationRole,
  type ProjectRole,
} from '../types';

import { granularityLabels, monthLabels } from './constants';

// example:  First Officer -> firstOfficer
export const transformToValue = (str: string) => {
  // Helper function to check if the string is already in camelCase
  const isCamelCase = (s: string) => /^[a-z][a-zA-Z]*$/.test(s);

  // Check if the string is already in camelCase
  if (isCamelCase(str)) {
    return str;
  }

  // Transform the string to camelCase if it's not already
  return str
    .split(/\s+/)
    .map(
      (word, index) =>
        index === 0
          ? word.charAt(0).toLowerCase() + word.slice(1).toLowerCase() // Lowercase first letter of the first word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Uppercase first letter of subsequent words
    )
    .join('')
    .trim(); // Join the transformed words into a single string
};

export const transformToLabel = (str: string) =>
  str
    .replace(/([A-Z])/g, (match, p1) => ` ${p1.toUpperCase()}`)
    .replace(/^./, (match) => match.toUpperCase())
    .replaceAll('_', ' ')
    .trim();

// Generating a list of years base on the current year, 6 years before and 20 years after
export const generateYearList = () => {
  const currentYear = new Date().getFullYear();

  const result = new Set([
    ...Array.from({ length: 5 }, (_, i) =>
      (currentYear - i).toString()
    ).reverse(),
    ...Array.from({ length: 20 }, (_, i) => (currentYear + i).toString()),
  ]);

  return Array.from(result).map((year) => ({
    label: year,
    value: year,
  }));
};

export const arrangeByBase = (items?: (TypeScenario | TypeModel)[]) => {
  if (!items) {
    return [];
  }

  const baseItemIndex = items.findIndex((item) => item.is_base);
  if (baseItemIndex > -1) {
    const [baseItem] = items.splice(baseItemIndex, 1);
    items.unshift(baseItem);
  }
  return items;
};

export const generateMonthList = () =>
  monthLabels.map((label, index) => ({
    label,
    value: String(index + 1),
  }));

export const generateGranularityList = () =>
  granularityLabels.map((label) => ({
    label,
    value: transformToValue(label),
  }));

export const generateWeekList = () =>
  Array.from({ length: 52 }, (_, i) => `${i + 1}`).map((label, index) => ({
    label: `W${label}`,
    value: String(index + 1),
  }));

export const transformDateDisplay = ({
  date,
  granularity,
  to,
}: {
  date?: string;
  granularity: Granularity;
  to: 'value' | 'label';
}) => {
  if (!date) {
    return '';
  }

  const firstIndexValue = date.split('-')[0];
  const year = date.split('-')[1];
  let result;

  const weekList = generateWeekList();
  const monthList = generateMonthList();

  const from = to === 'value' ? 'label' : 'value';

  switch (granularity) {
    case 'monthly':
      const month = monthList.find((month) => month[from] === firstIndexValue);

      if (!month) {
        return null;
      }

      result = `${month?.[to]}-${year}`;
      break;
    case 'weekly':
      const week = weekList.find((week) => week[from] === firstIndexValue);

      if (!week) {
        return null;
      }

      result = `${week?.[to]}-${year}`;
      break;
    default:
      result = date;
  }

  return result;
};

export const useCurrentOrganization = () => {
  const { data: organizations, isFetchedAfterMount: isFetchedOrganizations } =
    useGetOrganizations();

  const { orgId } = useGetRouteParams();

  const { data: user, isFetchedAfterMount: isFetchedUser } = useGetUser();

  const orgUsers = useUserStore((state) => state.orgUsers);

  const currentOrgId =
    user && !_.isEmpty(orgUsers) ? orgUsers?.[user.id]?.orgId : orgId;

  const currentOrg = useMemo(
    () =>
      organizations
        ? (organizations.find((o) => o.id === currentOrgId) ?? organizations[0])
        : null,
    [orgId, organizations, currentOrgId]
  );

  return {
    data: currentOrg,
    isFetched: isFetchedOrganizations && isFetchedUser,
  } as { data: TypeOrganization | null; isFetched: boolean };
};

export const useGetCurrentMember = () => {
  const { data: user } = useGetUser();
  const { orgId } = useGetRouteParams();

  const { data: members } = useGetOrgMembers({ orgId }, { enabled: !!orgId });

  const member = members?.find((member) => member.user.id === user?.id);

  return member;
};

export const useCurrentProject = (defaultProjectId?: number) => {
  const { projectId, orgId } = useGetRouteParams();

  const {
    data: project,
    isLoading,
    isRefetching,
  } = useGetProjectById(
    {
      projectId: projectId || defaultProjectId || NaN,
      orgId,
    },
    { enabled: (!!projectId || !!defaultProjectId) && !!orgId }
  );

  return {
    project,
    isLoading: isLoading || isRefetching,
  };
};

export const useCurrentMembership = () => {
  const { data: user } = useGetUser();

  const { data: organization } = useCurrentOrganization();

  const { projectId, orgId } = useGetRouteParams();

  const { data: org_members } = useGetOrgMembers(
    { orgId },
    { enabled: !!orgId && !!organization && isOrgAdmin(organization?.role) }
  );

  const member = org_members?.find((member) => member.user.id === user?.id);

  const projectMember = member?.project_memberships?.find(
    (m) => m.project.id === Number(projectId)
  );

  return {
    project: {
      role: projectMember?.role,
      ...projectMember?.project,
    },
    organization,
  } as {
    project: { id: number; name: string; role: ProjectRole };
    organization: {
      id: number;
      name: string;
      role: OrganizationRole;
      description: string;
    };
  };
};

export const getCompositionFileName = (contentDisposition: string) => {
  let filename = 'download.file';

  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
    );

    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return filename;
};

export const downloadFile = ({
  fileLink,
  filename,
}: {
  fileLink: string;
  filename: string;
}) => {
  const link = document.createElement('a');
  link.href = fileLink;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
